import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lock, CheckCircle2, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { requestSalonMagicLink, salonPasswordLogin } from "../lib/api";

export default function SalonLogin({ admin = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("password"); // "password" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onPasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      const res = await salonPasswordLogin(email.trim().toLowerCase(), password);
      const isAdmin = res?.salon?.role === "admin";
      const next = searchParams.get("next");
      navigate(isAdmin ? "/admin/dashboard" : (next || "/salon/admin"));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError("Too many attempts. Try again in 15 minutes, or use a magic link.");
      } else if (status === 401) {
        setError(typeof detail === "string" ? detail : "Invalid email or password.");
      } else if (!err?.response) {
        setError("Couldn't reach the server — it may be updating. Wait a minute and try again.");
      } else {
        setError((typeof detail === "string" && detail) || `Something went wrong (${status}). Try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const onMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      await requestSalonMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many sign-in requests. Try again in 15 minutes.");
      } else {
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-14 md:pt-16 min-h-screen flex items-center bg-bone" data-testid="salon-login-page">
      <div className="max-w-md w-full mx-auto px-5 md:px-8 py-12">
        <Link to="/" className="text-xs text-mocha hover:text-espresso transition-colors inline-flex items-center gap-2" data-testid="login-back">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back
        </Link>

        {!sent && (
          <>
            <div className="mt-8 text-center">
              <Lock size={18} strokeWidth={1.5} className="text-mocha mx-auto" />
              <h1 className="mt-4 font-serif text-3xl md:text-4xl text-ink leading-tight">{admin ? "Admin sign-in." : "Salon sign-in."}</h1>
              <p className="mt-2 text-sm text-mocha">
                {admin
                  ? "Sign in to the FYLLR Command Center."
                  : mode === "password"
                  ? "Sign in with your salon email and password."
                  : "We'll send a secure sign-in link to your registered salon email."}
              </p>
            </div>

            {/* Mode tabs */}
            <div className="mt-8 flex rounded-full border border-sand overflow-hidden" data-testid="login-mode-tabs">
              <button
                type="button"
                onClick={() => { setMode("password"); setError(""); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[12px] tracking-wide transition-colors ${
                  mode === "password" ? "bg-espresso text-bone" : "bg-bone text-ink/60 hover:text-ink"
                }`}
                data-testid="login-tab-password"
              >
                <KeyRound size={13} strokeWidth={1.5} /> Password
              </button>
              <button
                type="button"
                onClick={() => { setMode("magic"); setError(""); }}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[12px] tracking-wide transition-colors ${
                  mode === "magic" ? "bg-espresso text-bone" : "bg-bone text-ink/60 hover:text-ink"
                }`}
                data-testid="login-tab-magic"
              >
                <Mail size={13} strokeWidth={1.5} /> Magic link
              </button>
            </div>

            {/* Password mode */}
            {mode === "password" && (
              <form onSubmit={onPasswordLogin} className="mt-6 space-y-4" data-testid="salon-login-form">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Salon email</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="owner@studio.com"
                    className="w-full bg-bone border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                    data-testid="salon-login-email"
                  />
                </label>

                <label className="block">
                  <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Password</span>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 10 characters"
                    className="w-full bg-bone border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                    data-testid="salon-login-password"
                  />
                </label>

                {error && (
                  <p className="text-sm text-red-600" data-testid="salon-login-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-espresso text-bone py-3 text-sm transition-colors ${loading ? "opacity-60" : "hover:bg-ink"}`}
                  data-testid="salon-login-submit"
                >
                  {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} strokeWidth={1.5} />
                </button>

                <p className="text-center text-xs text-mocha/70">
                  No password yet?{" "}
                  <button type="button" onClick={() => setMode("magic")} className="underline hover:text-espresso" data-testid="login-switch-magic">
                    Sign in with magic link
                  </button>{" "}
                  and set one from your dashboard.
                </p>
              </form>
            )}

            {/* Magic link mode */}
            {mode === "magic" && (
              <form onSubmit={onMagicLink} className="mt-6 space-y-4" data-testid="salon-magic-form">
                <label className="block">
                  <span className="block text-[10px] tracking-[0.22em] uppercase text-mocha mb-1.5">Salon email</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="owner@studio.com"
                    className="w-full bg-bone border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px]"
                    data-testid="salon-magic-email"
                  />
                </label>

                {error && (
                  <p className="text-sm text-red-600" data-testid="salon-login-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full bg-espresso text-bone py-3 text-sm transition-colors ${loading ? "opacity-60" : "hover:bg-ink"}`}
                  data-testid="salon-magic-submit"
                >
                  {loading ? "Sending…" : "Send my sign-in link"} <ArrowRight size={14} strokeWidth={1.5} />
                </button>
              </form>
            )}

            <p className="mt-6 text-center text-xs text-mocha/70">
              Not on FYLLR yet?{" "}
              <Link to="/salon/onboard" className="underline hover:text-espresso" data-testid="login-onboard-link">
                Join FYLLR — it’s free
              </Link>.
            </p>
          </>
        )}

        {sent && (
          <div className="mt-10 text-center" data-testid="salon-login-sent">
            <CheckCircle2 size={22} strokeWidth={1.5} className="text-mocha mx-auto" />
            <h1 className="mt-4 font-serif text-3xl md:text-4xl text-ink leading-tight">Check your inbox.</h1>
            <p className="mt-3 text-sm text-mocha leading-relaxed">
              If <span className="text-espresso">{email}</span> is registered on FYLLR, a secure sign-in link is on its way. It expires in 15 minutes.
            </p>
            <p className="mt-6 text-xs text-mocha/70">
              Didn't get it?{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="underline hover:text-espresso"
                data-testid="salon-login-try-again"
              >
                Try a different email
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
